import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './state/store';
import { IntlProviderWrapper } from './i18n/IntlProviderWrapper';
import { ErrorBoundary } from './shared/components/ErrorBoundary';
import { usePendo } from './shared/hooks/usePendo';
import { AuthProvider } from './auth/AuthProvider';
import { RequireAuth } from './auth/guards/RequireAuth';
import { RequireAdmin } from './auth/guards/RequireAdmin';
import { AccessDeniedWatcher } from './auth/AccessDeniedWatcher';
import { RequireNoActiveWorkflow } from './features/activeWorkflow/RequireNoActiveWorkflow';
import { ActiveWorkflowBootstrap } from './features/activeWorkflow/ActiveWorkflowBootstrap';
import { PageLoader } from './shared/components/PageLoader';

const LoginPage = lazy(() => import('./features/auth/pages/LoginPage'));
const UploadPage = lazy(() => import('./features/upload/pages/UploadPage'));
const AnalysisPage = lazy(() => import('./features/analysis/pages/AnalysisPage'));
const TranslatePage = lazy(() => import('./features/translate/pages/TranslatePage'));
const CallbackPage = lazy(() => import('./auth/routes/CallbackPage'));
const AccessDeniedPage = lazy(() => import('./auth/routes/AccessDeniedPage'));
const XtmCloudConfigPage = lazy(() => import('./features/admin/pages/XtmCloudConfigPage'));
const TranslatedBatchesPage = lazy(() => import('./features/translated-batches/pages/TranslatedBatchesPage'));
const TranslatedBatchPage = lazy(() => import('./features/translated-batches/pages/TranslatedBatchPage'));

function AppRoutes() {
  usePendo();

  return (
    <ErrorBoundary>
      <AccessDeniedWatcher />
      <ActiveWorkflowBootstrap />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<CallbackPage />} />
          <Route path="/access-denied" element={<AccessDeniedPage />} />
          <Route
            path="/upload"
            element={
              <RequireAuth>
                <RequireNoActiveWorkflow>
                  <UploadPage />
                </RequireNoActiveWorkflow>
              </RequireAuth>
            }
          />
          <Route path="/analysis" element={<RequireAuth><AnalysisPage /></RequireAuth>} />
          <Route path="/translate" element={<RequireAuth><TranslatePage /></RequireAuth>} />
          <Route path="/translated-files" element={<RequireAuth><TranslatedBatchesPage /></RequireAuth>} />
          <Route path="/translated-files/:batchId" element={<RequireAuth><TranslatedBatchPage /></RequireAuth>} />
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <RequireAdmin fallback={<AccessDeniedPage />}>
                  <XtmCloudConfigPage />
                </RequireAdmin>
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/upload" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <IntlProviderWrapper>
        <BrowserRouter basename={import.meta.env.BASE_URL}>
          <AuthProvider>
            {/* The page is the DS's subtle surface (#f7f7f8), not `background`
                (#ffffff). Cards, the header and inputs are all `bg-card`/
                `bg-background` — white — so painting the page white too left them
                separated by a 1px border alone. The design system's own frame
                puts white cards on a light grey page. */}
            <div className="xtm-express-root min-h-screen flex flex-col bg-xtm-surface">
              <AppRoutes />
            </div>
          </AuthProvider>
        </BrowserRouter>
      </IntlProviderWrapper>
    </Provider>
  );
}
